import { useEffect, useRef, useState } from 'react'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { CrmApiError } from '../../http'
import { toastMerged } from '../../merges/useUndoMerge'
import {
  getMergePreview,
  mergeOrganisations,
  type DuplicateOrganisation,
  type MergeField,
  type MergePreview,
} from './api'

const FIELD_LABELS: Record<MergeField, string> = {
  name: 'Naam',
  description: 'Omschrijving',
  website: 'Website',
  kvk_number: 'KvK-nummer',
  email: 'E-mail',
  phone: 'Telefoon',
  street: 'Straat',
  house_number: 'Huisnummer',
  postcode: 'Postcode',
  city: 'Plaats',
  country: 'Land',
  linkedin_url: 'LinkedIn',
  category_id: 'Categorie',
  owner_user_id: 'Eigenaar',
  status: 'Status',
  summary_html: 'Over de organisatie',
  relation_since: 'Relatie sinds',
}

type Side = 'survivor' | 'other'

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === ''

/** The text of a stored value; the summary is HTML and shows as its text. */
function valueText(field: MergeField, value: unknown, readable?: string | null): string {
  if (isEmpty(value)) return '—'
  if (readable) return readable
  const text = String(value)
  if (field !== 'summary_html') return text
  return new DOMParser().parseFromString(text, 'text/html').body.textContent?.trim() || '—'
}

function countText(count: number, one: string, many: string): string {
  return count === 1 ? `1 ${one}` : `${count} ${many}`
}

function describeOrganisation(organisation: DuplicateOrganisation): string {
  const parts = [organisation.domains[0] ?? organisation.website, organisation.city].filter(Boolean)
  return [organisation.name, ...parts].join(' · ')
}

/**
 * Merge two organisations (spec-63 E6, design §7.8): pick the one that stays, choose per
 * differing field whose value wins, see what moves, confirm. The preview's `updated_at` stamps go
 * back with the merge, so a change in between is refused (409) instead of overwritten.
 */
export function MergeOrganisationsDialog({
  firstId,
  secondId,
  survivorId: preferredSurvivorId,
  onClose,
  onMerged,
  onUndone,
}: {
  firstId: string
  secondId: string
  /** The one that stays, when the caller already knows (the organisation page); otherwise decided by the contact count. */
  survivorId?: string
  onClose: () => void
  onMerged: (survivor: { id: string; name: string }) => void
  /** After "Ongedaan maken" in the toast. */
  onUndone?: () => void
}) {
  const [survivorId, setSurvivorId] = useState(preferredSurvivorId ?? firstId)
  const otherId = survivorId === firstId ? secondId : firstId
  const [preview, setPreview] = useState<MergePreview | null>(null)
  const [choices, setChoices] = useState<Partial<Record<MergeField, Side>>>({})
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The default survivor is decided once, from the first answer, unless the caller chose it.
  const decided = useRef(preferredSurvivorId !== undefined)

  useEffect(() => {
    let cancelled = false
    getMergePreview(survivorId, otherId)
      .then((answer) => {
        if (cancelled) return
        if (!decided.current) {
          decided.current = true
          if (answer.other.members_count > answer.survivor.members_count) {
            setSurvivorId(otherId)
            return
          }
        }
        const initial: Partial<Record<MergeField, Side>> = {}
        for (const difference of answer.fields) {
          // An empty survivor value is filled from the other organisation anyway.
          initial[difference.field] = isEmpty(difference.survivor) ? 'other' : 'survivor'
        }
        setChoices(initial)
        setPreview(answer)
        setLoadError(null)
      })
      .catch((caught: unknown) => {
        if (!cancelled) setLoadError(caught instanceof Error ? caught.message : 'Laden mislukt.')
      })
    return () => {
      cancelled = true
    }
  }, [survivorId, otherId, reloadKey])

  async function merge(current: MergePreview) {
    setBusy(true)
    setError(null)
    try {
      const survivor = await mergeOrganisations({
        survivor_id: current.survivor.id,
        other_id: current.other.id,
        take_from_other: current.fields
          .filter((difference) => choices[difference.field] === 'other')
          .map((difference) => difference.field),
        expected: {
          survivor_updated_at: current.survivor_updated_at,
          other_updated_at: current.other_updated_at,
        },
      })
      setConfirming(false)
      toastMerged('organisatie', survivor.id, `Samengevoegd met ${current.survivor.name}`, onUndone)
      onMerged({ id: survivor.id, name: current.survivor.name })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Samenvoegen mislukt.')
      if (caught instanceof CrmApiError && caught.status === 409) {
        // Someone changed one of them: show the news and let the person decide again.
        setConfirming(false)
        setReloadKey((key) => key + 1)
      }
    } finally {
      setBusy(false)
    }
  }

  const moves = preview?.moves
  const contacts = moves ? moves.members_moved + moves.members_folded : 0
  return (
    <>
      <Dialog open onOpenChange={(next) => (!next && !busy ? onClose() : undefined)}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Organisaties samenvoegen</DialogTitle>
            <DialogDescription>
              Kies welke organisatie blijft bestaan. De andere verdwijnt; alles wat eraan hangt gaat mee.
            </DialogDescription>
          </DialogHeader>
          {loadError ? (
            <p role="alert" className="text-sm text-destructive">
              {loadError}
            </p>
          ) : !preview || !moves ? (
            <div className="flex flex-col gap-2" aria-hidden>
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <fieldset className="flex flex-col gap-1">
                <legend className="text-sm font-semibold">Welke blijft over?</legend>
                {[preview.survivor, preview.other].map((organisation) => (
                  <label key={organisation.id} className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="survivor"
                      checked={organisation.id === preview.survivor.id}
                      onChange={() => setSurvivorId(organisation.id)}
                      className="mt-1"
                    />
                    <span>
                      {describeOrganisation(organisation)}
                      <span className="text-muted-foreground">
                        {' '}
                        ({countText(organisation.members_count, 'contactpersoon', 'contactpersonen')})
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>
              {preview.fields.length > 0 ? (
                <section className="flex flex-col gap-2">
                  <h3 className="text-sm font-semibold">Verschillende gegevens</h3>
                  {preview.fields.map((difference) => {
                    const name = `field-${difference.field}`
                    const label = FIELD_LABELS[difference.field]
                    return (
                      <fieldset key={difference.field} className="flex flex-col gap-1 text-sm">
                        <legend className="font-medium">{label}</legend>
                        <label className="flex items-start gap-2">
                          <input
                            type="radio"
                            name={name}
                            aria-label={`${label}: ${valueText(difference.field, difference.survivor, difference.survivor_text)} (blijft)`}
                            checked={choices[difference.field] === 'survivor'}
                            onChange={() => setChoices((current) => ({ ...current, [difference.field]: 'survivor' }))}
                            className="mt-1"
                          />
                          <span>{valueText(difference.field, difference.survivor, difference.survivor_text)}</span>
                        </label>
                        <label className="flex items-start gap-2">
                          <input
                            type="radio"
                            name={name}
                            aria-label={`${label}: ${valueText(difference.field, difference.other, difference.other_text)} (andere)`}
                            checked={choices[difference.field] === 'other'}
                            onChange={() => setChoices((current) => ({ ...current, [difference.field]: 'other' }))}
                            className="mt-1"
                          />
                          <span>{valueText(difference.field, difference.other, difference.other_text)}</span>
                        </label>
                        {isEmpty(difference.survivor) ? (
                          <p className="text-xs text-muted-foreground">
                            Leeg bij de organisatie die blijft: de waarde van de andere wordt overgenomen.
                          </p>
                        ) : null}
                      </fieldset>
                    )
                  })}
                </section>
              ) : null}
              <section className="flex flex-col gap-1">
                <h3 className="text-sm font-semibold">Wat er verhuist</h3>
                <ul className="list-disc pl-5 text-sm">
                  <li>{countText(moves.members_moved, 'contactpersoon verhuist', 'contactpersonen verhuizen')}</li>
                  {moves.members_folded > 0 ? (
                    <li>
                      {countText(moves.members_folded, 'contactpersoon stond bij beide', 'contactpersonen stonden bij beide')}
                      {' '}en wordt samengevoegd
                    </li>
                  ) : null}
                  <li>{countText(moves.activities, 'activiteit', 'activiteiten')}</li>
                  <li>{countText(moves.agreements, 'afspraak', 'afspraken')}</li>
                  <li>{countText(moves.tasks, 'taak', 'taken')}</li>
                  <li>
                    {countText(moves.labels_moved, 'label', 'labels')}
                    {moves.labels_dropped > 0 ? ` (${moves.labels_dropped} al aanwezig)` : ''}
                  </li>
                  {moves.saved_views > 0 ? (
                    <li>{countText(moves.saved_views, 'opgeslagen weergave wordt bijgewerkt', 'opgeslagen weergaven worden bijgewerkt')}</li>
                  ) : null}
                </ul>
              </section>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Annuleren
            </Button>
            <Button type="button" disabled={!preview || busy} onClick={() => setConfirming(true)}>
              Samenvoegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {preview && moves ? (
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Samenvoegen?"
          description={`${preview.other.name} samenvoegen met ${preview.survivor.name}? ${countText(contacts, 'contactpersoon', 'contactpersonen')}, ${countText(moves.activities, 'activiteit', 'activiteiten')}, ${countText(moves.agreements, 'afspraak', 'afspraken')} en ${countText(moves.tasks, 'taak', 'taken')} gaan naar ${preview.survivor.name}. ${preview.other.name} verdwijnt.`}
          confirmLabel="Samenvoegen"
          busy={busy}
          error={error}
          onConfirm={() => void merge(preview)}
        />
      ) : null}
    </>
  )
}
